import { ComponentProps } from "react";

import {
  faSpinner,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Breakdown } from "src/base-components/Breakdown";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { SimulationPostprocessingStatus } from "src/clients/jobs";
import { JobRun, JobRunStatus, JobRunStatusType } from "src/jobs/types";
import { formatNumber } from "src/utils/numbers";
import { assertUnreachable } from "src/utils/typeUtils";

type SimulationStatusIndicatorProps = Pick<
  JobRun,
  | "success_rows_count"
  | "failed_rows_count"
  | "ignored_rows_count"
  | "total_rows_count"
  | "status"
  | "simulation_job_run_details"
>;

type SimulationDataType = "success" | "ignored" | "failed" | "not_started";

type SimulationData = Array<
  ComponentProps<typeof Breakdown>["bars"][number] & {
    type: SimulationDataType;
    displayText: string;
  }
>;
// Satisfies allows us to complain with <Breakdown/> while
// having the option of specifying additional fields
// and don't get a TypeScript error

type SimulationStatusIndicatorButtonProps = {
  simulationData: SimulationData;
  simulationStatus: JobRunStatus;
  simulationPostProcessingStatus:
    | SimulationPostprocessingStatus
    | null
    | undefined;
};

const barToPillColors = {
  green: "green" as const,
  red: "red" as const,
  gray: "gray" as const,
  "light-gray": "outlined-white" as const,
  neutral: "outlined-white" as const,
  indigo: "indigo-light" as const,
  yellow: "yellow" as const,
};

const SimulationStatusIndicatorButton: React.FC<
  SimulationStatusIndicatorButtonProps
> = ({ simulationData, simulationStatus, simulationPostProcessingStatus }) => {
  let content: React.ReactNode = null;

  const simulationStatusType = simulationStatus.type;

  switch (simulationStatusType) {
    case JobRunStatusType.Running:
    case JobRunStatusType.Writing:
      content = (
        <Pill variant="indigo-light" onClick={() => {}}>
          <Pill.Icon icon={faSpinner} spin />
          <Pill.Text>Running</Pill.Text>
        </Pill>
      );
      break;
    case JobRunStatusType.Completed:
      switch (simulationPostProcessingStatus) {
        case SimulationPostprocessingStatus.SCHEDULED:
          content = (
            <Pill variant="indigo-light" onClick={() => {}}>
              <Pill.Icon icon={faSpinner} spin />
              <Pill.Text>Generating analytics</Pill.Text>
            </Pill>
          );
          break;
        case SimulationPostprocessingStatus.FAILED:
          content = (
            <Pill variant="red" onClick={() => {}}>
              <Pill.Icon icon={faTriangleExclamation} />
              <Pill.Text>Processing failed</Pill.Text>
            </Pill>
          );
          break;
        case SimulationPostprocessingStatus.COMPLETED:
        case null:
        case undefined:
          content = (
            <div
              className="flex h-4 w-full cursor-pointer items-center gap-x-1.5"
              data-loc="run-decisions-by-status-button"
            >
              <div className="flex-1">
                <Breakdown bars={simulationData} />
              </div>
            </div>
          );
          break;
        default:
          assertUnreachable(simulationPostProcessingStatus);
          break;
      }
      break;
    case JobRunStatusType.Failed:
      content = (
        <Pill variant="red" onClick={() => {}}>
          <Pill.Icon icon={faTriangleExclamation} />
          <Pill.Text>{simulationStatus.error_title}</Pill.Text>
        </Pill>
      );
      break;
    case JobRunStatusType.Stopped:
      content = (
        <Pill variant="red" onClick={() => {}}>
          <Pill.Icon icon={faTriangleExclamation} />
          <Pill.Text>Simulation stopped</Pill.Text>
        </Pill>
      );
      break;
    default:
      assertUnreachable(simulationStatusType);
      content = (
        <Pill variant="red" onClick={() => {}}>
          <Pill.Icon icon={faTriangleExclamation} />
          <Pill.Text>Unknown status</Pill.Text>
        </Pill>
      );
  }

  return <div data-loc="simulation-status-indicator-button">{content}</div>;
};

const SimulationsStatusPopoverBody: React.FC<{
  simulationData: SimulationData;
  totalRowsCount: number;
}> = ({ simulationData, totalRowsCount }) => {
  return (
    <>
      <div className="border-gray-100 border-b px-5 pt-4 pb-3">
        <h1 className="text-left font-inter-semibold-16px text-gray-800">
          Decisions by status
        </h1>
      </div>
      <div className="px-5 pt-4 pb-6">
        <div className="border border-gray-100 text-left text-gray-800">
          <div className="flex h-8 bg-gray-50 font-inter-medium-12px">
            <div className="flex min-w-[127px] items-center border-gray-100 border-r px-2">
              Status
            </div>
            <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
              Decisions
            </div>
            <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2"></div>
            <div className="flex min-w-[169px] items-center px-2"></div>
          </div>
          {simulationData.map((entry) => (
            <div
              key={entry.type}
              className="flex h-8 border-gray-100 border-t font-inter-normal-12px"
            >
              <div className="flex min-w-[127px] items-center border-gray-100 border-r px-1.5">
                <Pill variant={barToPillColors[entry.color]}>
                  <Pill.Text>{entry.displayText}</Pill.Text>
                </Pill>
              </div>
              <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
                <p
                  className="flex-1 text-right font-inter-normal-12px text-gray-800"
                  data-loc={`run-decisions-by-status-button-${entry.type}-count`}
                >
                  {formatNumber(entry.count)}
                </p>
              </div>
              <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2">
                <p
                  className="flex-1 text-right font-inter-normal-12px text-gray-500"
                  data-loc={`run-decisions-by-status-button-${entry.type}-percentage`}
                >
                  (
                  {formatNumber(
                    totalRowsCount !== 0 ? entry.count / totalRowsCount : 0,
                    {
                      style: "percent",
                    },
                  )}
                  )
                </p>
              </div>
              <div className="flex min-w-[169px] items-center px-2">
                <Breakdown bars={simulationData} highlightOnly={entry.color} />
              </div>
            </div>
          ))}
          <div className="flex h-8 border-gray-100 border-t font-inter-normal-12px">
            <div className="flex min-w-[127px] items-center border-gray-100 border-r px-1.5 font-inter-normal-12px text-gray-800">
              Total
            </div>
            <div className="flex min-w-0 flex-1 items-center border-gray-100 border-r px-2">
              <p className="flex-1 text-right font-inter-semibold-13px text-gray-800">
                {formatNumber(totalRowsCount)}
              </p>
            </div>
            <div className="flex min-w-[62px] items-center border-gray-100 border-r px-2">
              <p className="flex-1 text-right font-inter-normal-12px text-gray-500">
                (100%)
              </p>
            </div>
            <div className="flex min-w-[169px] items-center px-2">
              <Breakdown bars={simulationData} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const SimulationStatusIndicator: React.FC<
  SimulationStatusIndicatorProps
> = ({
  failed_rows_count,
  ignored_rows_count,
  success_rows_count,
  total_rows_count,
  status,
  simulation_job_run_details,
}) => {
  const processedCount =
    success_rows_count + ignored_rows_count + failed_rows_count;

  const data: SimulationData = [
    {
      type: "success",
      displayText: "Successful",
      count: success_rows_count,
      color: "green",
    },
    {
      type: "failed",
      displayText: "Failed",
      count: failed_rows_count,
      color: "red",
    },
    {
      type: "ignored",
      displayText: "Ignored",
      count: ignored_rows_count,
      color: "gray",
    },
    {
      type: "not_started",
      displayText: "Waiting",
      count: total_rows_count - processedCount,
      color: "light-gray",
    },
  ];

  return (
    <CustomPopover
      button={
        <SimulationStatusIndicatorButton
          data-loc="simulation-status-indicator-button"
          simulationData={data}
          simulationPostProcessingStatus={
            simulation_job_run_details?.post_processing_status
          }
          simulationStatus={status}
        />
      }
      offsetX={24}
      placement="right"
      wrapperClassName="w-full"
    >
      <div className="relative w-122 cursor-default">
        <CustomPopover.Button
          className="absolute top-4 right-4"
          data-loc="close-decisions-by-status"
        >
          <Icon color="text-gray-500" icon={faXmark} size="xs" />
        </CustomPopover.Button>
        <SimulationsStatusPopoverBody
          simulationData={data}
          totalRowsCount={total_rows_count}
        />
      </div>
    </CustomPopover>
  );
};
